import React, { useState, useRef, useEffect } from "react";
import { Box, Button, CircularProgress, TextField, Typography, IconButton } from "@mui/material";
import { LightMode as LightModeIcon, DarkMode as DarkModeIcon } from "@mui/icons-material";
import ReactMarkdown from "react-markdown";
import io from "socket.io-client";
import "./App.css";

// Configuración del socket
const socket = io("https://mitserver.app:2942"); // Cambia la URL por la de tu servidor

export default function ChatCandles() {
	const [messages, setMessages] = useState([]);
	const [input, setInput] = useState("");
	const [loading, setLoading] = useState(false);
	const [thinking, setThinking] = useState(false);
	const [dots, setDots] = useState("");
	const [user, setUser] = useState(null);
	const [connected, setConnected] = useState(false);
	const [theme, setTheme] = useState("dark"); // Add theme state
	const scrollRef = useRef(null);
	const inputRef = useRef(null);

	// Generar ID de usuario random
	const generateUserId = () => {
		return "user_" + Math.random().toString(36).substr(2, 9);
	};

	// Efecto para los puntos de "pensando"
	useEffect(() => {
		if (!thinking) return;
		const interval = setInterval(() => {
			setDots((prev) => (prev.length >= 3 ? "" : prev + "."));
		}, 300);
		return () => clearInterval(interval);
	}, [thinking]);

	// Auto-scroll to bottom when new messages arrive
	useEffect(() => {
		if (scrollRef.current) {
			scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
		}
	}, [messages]);

	// Inicializar usuario y cargar historial
	useEffect(() => {
		// Listeners de conexión
		const handleChatFunctionResult = ({ question, function: funcName, result }) => {
			setThinking(false);
			setDots("");
			setLoading(false);

			let reply;
			try {
				const json = JSON.parse(result);

				if (json?.error) {
					reply = `❌ ${json.error}`;
				} else if (json?.message) {
					reply = json.message;
				} else if (json?.success) {
					reply = "✅ Operación realizada correctamente.";
				} else if (Array.isArray(json?.results) && json.results.length > 0) {
					reply = `📋 Se encontraron ${json.results.length} resultados.`;
				} else {
					reply = typeof result === "string" ? result : JSON.stringify(result);
				}
			} catch (err) {
				reply = result || "✅ Listo.";
			}

			setMessages((prev) => prev.filter((m) => m.role !== "system").concat({ role: "assistant", content: reply }));
		};

		socket.on("connect", () => {
			console.log("✅ Conectado al servidor:", socket.id);
			setConnected(true);
		});

		socket.on("disconnect", () => {
			console.log("❌ Desconectado del servidor");
			setConnected(false);
		});

		socket.on("connect_error", (error) => {
			console.error("❌ Error de conexión:", error);
			setConnected(false);
		});

		// Buscar usuario en localStorage o crear uno nuevo
		let userData = localStorage.getItem("chatUser");

		if (userData) {
			userData = JSON.parse(userData);
		} else {
			userData = { id: generateUserId() };
			localStorage.setItem("chatUser", JSON.stringify(userData));
		}

		setUser(userData);

		// Cargar historial de chat
		socket.emit("loadChatHistory", {
			userId: userData.id,
		});

		// Socket event listeners
		const handleChatHistory = (msgs) => {
			setMessages(msgs);
		};

		const handleChatReply = (res) => {
			console.log("handleChatReply", res);
			const reply = res?.reply || "No se obtuvo respuesta.";
			const qrCode = res?.qrCode; // Capturar el QR code

			setThinking(false);
			setDots("");
			setLoading(false);

			// Filtrar mensajes del sistema y agregar respuesta
			setMessages((prev) =>
				prev
					.filter((m) => m.role !== "system")
					.concat({
						role: "assistant",
						content: reply,
						qrCode: qrCode, // Agregar el QR code al mensaje
					})
			);

			// Enfocar input después de un momento
			setTimeout(() => {
				if (inputRef.current) inputRef.current.focus();
			}, 100);
		};

		const handleChatToolCall = ({ function: funcName, arguments: argsStr }) => {
			setThinking(true);
			setDots(".");
			setMessages((prev) => [...prev, { role: "system", content: "🔎 Consultando datos..." }]);

			// Emitir resultado de función (puedes personalizar esto)
			socket.emit("chatFunctionResult", {
				question: input,
				function: funcName,
				result: JSON.stringify({ processed: true }),
				userId: userData.id,
			});
		};

		const handleTokensUsed = ({ used, total }) => {
			console.log(`Tokens usados: ${used}/${total}`);
		};

		// Registrar listeners
		socket.on("chatHistory", handleChatHistory);
		socket.on("chatReply", handleChatReply);
		socket.on("chatToolCall", handleChatToolCall);
		socket.on("tokensUsed", handleTokensUsed);
		socket.on("chatFunctionResult", handleChatFunctionResult);

		// Cleanup
		return () => {
			socket.off("chatHistory", handleChatHistory);
			socket.off("chatReply", handleChatReply);
			socket.off("chatToolCall", handleChatToolCall);
			socket.off("tokensUsed", handleTokensUsed);
			socket.off("chatFunctionResult", handleChatFunctionResult);
		};
	}, []);

	// Handle sending messages
	const handleSend = () => {
		if (!input.trim() || loading || !user) return;

		setLoading(true);
		const userMessage = input.trim();

		// Add user message to chat immediately
		setMessages((prev) => [...prev, { role: "user", content: userMessage }]);

		// Emit to server
		socket.emit("chat", {
			question: userMessage,
			userId: user.id,
		});

		// Clear input
		setInput("");
	};

	// Handle Enter key press
	const handleKeyDown = (e) => {
		if (e.key === "Enter" && !loading) {
			e.preventDefault();
			handleSend();
		}
	};

	// Helper function to determine if dark mode
	const isDarkMode = theme === "dark";

	return (
		<Box
			sx={{
				width: "100%",
				height: "100dvh",
				bgcolor: isDarkMode ? "#121212" : "#f5f5f5",
				borderLeft: `1px solid ${isDarkMode ? "#333" : "#e0e0e0"}`,
				display: "flex",
				flexDirection: "column",
				fontFamily: "'Orbitron', sans-serif",
				borderRadius: { xs: "0px", sm: "10px" },
				boxShadow: isDarkMode ? "inset 0 0 10px #000, inset 0 0 20px rgba(69, 153, 217, 0.9)" : "0 4px 20px rgba(0,0,0,0.1)",
				transition: "all 0.3s ease",
				touchAction: "none",
				resize: "none",
				overflow: "hidden",
				position: "fixed",
				top: 0,
				left: 0,
			}}>
			{/* Header */}
			<Box
				sx={{
					px: { xs: 2, sm: 3 },
					pt: 2,
					pb: 1.5,
					bgcolor: isDarkMode ? "#1E1E1E" : "#ffffff",
					borderBottom: `1px solid ${isDarkMode ? "#333" : "#e0e0e0"}`,
				}}>
				<Box
					sx={{
						display: "flex",
						justifyContent: "space-between",
						alignItems: "center",
						flexWrap: "wrap",
						gap: 1,
					}}>
					<Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%" }}>
						<Box sx={{ display: "flex", alignItems: "center", gap: 1, ml: 0 }}>
							<Box
								sx={{
									width: 8,
									height: 8,
									borderRadius: "50%",
									bgcolor: connected ? "#4CAF50" : "#f44336",
									transition: "all 0.3s ease",
								}}
							/>
							<Typography
								sx={{
									color: connected ? (isDarkMode ? "#00f2ff" : "#2196F3") : isDarkMode ? "#ff6666" : "#f44336",
									fontFamily: "'Orbitron', sans-serif",
									fontSize: { xs: "0.55rem", sm: "0.6rem" },
								}}>
								{connected ? "Conectado" : "Desconectado"}
							</Typography>
						</Box>
						<IconButton
							onClick={() => setTheme((prevTheme) => (prevTheme === "dark" ? "light" : "dark"))}
							sx={{
								color: isDarkMode ? "#fff" : "#333",
								p: 0.5,
								mr: 0,
							}}>
							{isDarkMode ? <LightModeIcon fontSize="small" /> : <DarkModeIcon fontSize="small" />}
						</IconButton>
					</Box>

					<Typography
						sx={{
							color: isDarkMode ? "#fff" : "#000",
							fontFamily: "'Orbitron', sans-serif",
							fontSize: { xs: "1.2rem", sm: "1.4rem" },
							fontWeight: 700,
							textTransform: "uppercase",
							letterSpacing: "2px",
							textShadow: isDarkMode ? "0 0 10px #00f2ff" : "none",
							textAlign: "center",
						}}>
						Reservas Hotel Le Farrier
					</Typography>
				</Box>
			</Box>

			{/* Messages Container */}
			<Box
				ref={scrollRef}
				sx={{
					flexGrow: 1,
					overflowY: "auto",
					p: { xs: 1, sm: 2 },
					display: "flex",
					flexDirection: "column",
					gap: 1,
					bgcolor: isDarkMode ? "#121212" : "#fafafa",
					WebkitOverflowScrolling: "touch",
				}}>
				{messages.map((msg, i) => (
					<Box
						key={i}
						sx={{
							alignSelf: msg.role === "user" ? "flex-end" : msg.role === "system" ? "center" : "flex-start",
							maxWidth: { xs: "95%", sm: "85%" },
							px: { xs: 1.5, sm: 2 },
							py: 1,
							borderRadius: msg.role === "user" ? "20px 0px 20px 20px" : msg.role === "system" ? "10px" : "0px 20px 20px 20px",
							background:
								msg.role === "user"
									? isDarkMode
										? "rgba(69, 153, 217, 0.15)"
										: "rgba(33, 150, 243, 0.1)"
									: msg.role === "system"
									? isDarkMode
										? "rgba(255,255,255,0.1)"
										: "rgba(0,0,0,0.05)"
									: isDarkMode
									? "rgba(0, 242, 255, 0.08)"
									: "rgba(3, 169, 244, 0.1)",
							color: isDarkMode ? "#fff" : "#333",
							fontSize: { xs: "0.85rem", sm: "0.9rem" },
							fontFamily: "'Orbitron', sans-serif",
							boxShadow:
								msg.role === "user"
									? isDarkMode
										? "inset 0 0 15px #4599d9"
										: "0 2px 10px rgba(33, 150, 243, 0.2)"
									: msg.role === "system"
									? isDarkMode
										? "inset 0 0 15px #aaa"
										: "0 2px 10px rgba(0,0,0,0.1)"
									: isDarkMode
									? "inset 0 0 15px #00f2ff"
									: "0 2px 10px rgba(3, 169, 244, 0.2)",
							textAlign: msg.role === "system" ? "center" : "left",
							transition: "all 0.3s ease",
						}}>
						<ReactMarkdown
							skipHtml
							children={msg.role === "system" && msg.content.startsWith("🔎 Analizando") ? `${msg.content}${dots}` : msg.content}
						/>
						{msg.qrCode && (
							<Box sx={{ mt: 1, textAlign: "center" }}>
								<img
									src={msg.qrCode}
									alt="Código QR de la reserva"
									style={{
										maxWidth: "200px",
										maxHeight: "200px",
										border: `2px solid ${isDarkMode ? "#00f2ff" : "#2196F3"}`,
										borderRadius: "8px",
										backgroundColor: "#fff",
										padding: "8px",
										transition: "all 0.3s ease",
									}}
								/>
							</Box>
						)}
					</Box>
				))}
			</Box>

			{/* Input Container */}
			<Box
				sx={{
					p: { xs: 1, sm: 1.5 },
					borderTop: `1px solid ${isDarkMode ? "#333" : "#e0e0e0"}`,
					display: "flex",
					flexDirection: "column",
					gap: 0.5,
					bgcolor: isDarkMode ? "#1E1E1E" : "#ffffff",
				}}>
				<Box display="flex" gap={1}>
					<TextField
						inputRef={inputRef}
						sx={{
							flexGrow: 1,
							background: isDarkMode ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.02)",
							borderRadius: 2,
							fontSize: { xs: "0.85rem", sm: "0.9rem" },
							"& .MuiOutlinedInput-root": {
								fontFamily: "'Orbitron', sans-serif",
								"& fieldset": {
									borderColor: isDarkMode ? "#333" : "#e0e0e0",
								},
								"&:hover fieldset": {
									borderColor: isDarkMode ? "#4599d9" : "#2196F3",
								},
								"&.Mui-focused fieldset": {
									borderColor: isDarkMode ? "#4599d9" : "#2196F3",
								},
							},
						}}
						variant="outlined"
						size="small"
						placeholder="Escribí tu pregunta..."
						value={input}
						onChange={(e) => setInput(e.target.value)}
						onKeyDown={handleKeyDown}
						disabled={loading || !user}
						inputProps={{
							style: {
								color: isDarkMode ? "#fff" : "#333",
								fontFamily: "'Orbitron', sans-serif",
								fontSize: "0.9rem",
							},
							autoComplete: "off",
							autoCorrect: "off",
							autoCapitalize: "off",
							spellCheck: "false",
						}}
					/>
					<Button
						variant="contained"
						onClick={handleSend}
						disabled={loading || !input.trim() || !user}
						sx={{
							color: "#fff",
							background: isDarkMode ? "rgba(69, 153, 217, 0.3)" : "#2196F3",
							fontFamily: "'Orbitron', sans-serif",
							boxShadow: isDarkMode ? "inset 0 0 5px #4599d9, inset 0 0 10px #4599d9" : "0 2px 10px rgba(33, 150, 243, 0.3)",
							fontSize: { xs: "0.85rem", sm: "0.9rem" },
							"&:hover": {
								background: isDarkMode ? "rgba(69, 153, 217, 0.4)" : "#1976D2",
							},
							transition: "all 0.3s ease",
						}}>
						{loading ? <CircularProgress size={20} sx={{ color: "#fff" }} /> : "Enviar"}
					</Button>
				</Box>
			</Box>
		</Box>
	);
}
